import {useCallback, useEffect, useMemo, useState} from 'react';

import {getAvailableAccessRights} from 'src/admin-rights-service/components/permission-tree/utils/getAvailableAccessRights';
import {getAdminActiveProfile} from 'src/openapi/admin/apis/GetAdminActiveProfileApi';
import {getProfileAccessRights} from 'src/openapi/admin/apis/GetProfileAccessRightsApi';
import {useAppDispatch} from 'src/store/hooks';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import type React from 'react';
import type {Error} from 'src/openapi/admin/models/Error';
import type {ProfileRecord} from 'src/openapi/admin/models/ProfileRecord';

type Props = {
    userId: number,
    onPermissionsChanged: React.Dispatch<React.SetStateAction<string[]>>,
};

export const useAdminRoleSelect = ({userId, onPermissionsChanged}: Props) => {
    const apiDispatch = useAppDispatch();

    const [isLoading, setLoading] = useState(true);
    const [loadingError, setLoadingError] = useState<Error | null>(null);

    const [initialRole, setInitialRole] = useState<ProfileRecord | null>(null);
    const [role, setRole] = useState<ProfileRecord | null>(null);
    const [defaultAdminPermissions, setDefaultAdminPermissions] = useState<string[]>([]);

    useEffect(() => {
        const loadAdminRole = async () => {
            setLoading(true);
            const roleResponse = await apiDispatch(getAdminActiveProfile({adminId: userId}));
            if (roleResponse.payload.success && roleResponse.payload.data) {
                const loadedRole = roleResponse.payload.data;
                setInitialRole(loadedRole);
                setRole(loadedRole);

                const defaultAdminPermissionsResponse = await apiDispatch(getProfileAccessRights({
                    profileId: loadedRole.profileId,
                }));
                if (defaultAdminPermissionsResponse.payload.success) {
                    const availableAdminPermissions =
                        getAvailableAccessRights(defaultAdminPermissionsResponse.payload.data);
                    setDefaultAdminPermissions(availableAdminPermissions);
                }
            }

            if (roleResponse.payload.success && !roleResponse.payload.data) {
                setLoadingError(roleResponse.payload.data);
            }

            setLoading(false);
        };

        loadAdminRole();
    }, [userId, apiDispatch]);

    const changeRole = useCallback(async (newRole: ProfileRecord | null) => {
        setRole(newRole);

        if (newRole) {
            setLoading(true);
            const defaultAdminPermissionsResponse = await apiDispatch(getProfileAccessRights({
                profileId: newRole.profileId,
            }));
            if (defaultAdminPermissionsResponse.payload.success) {
                const availableAdminPermissions =
                    getAvailableAccessRights(defaultAdminPermissionsResponse.payload.data);
                setDefaultAdminPermissions(availableAdminPermissions);
                onPermissionsChanged(availableAdminPermissions);
            }

            if (defaultAdminPermissionsResponse.payload.error) {
                setLoadingError(defaultAdminPermissionsResponse.payload.data);
            }
            setLoading(false);
        } else {
            onPermissionsChanged([]);
        }
    }, [onPermissionsChanged, apiDispatch]);

    const discardAdminRole = useCallback(async () => {
        setRole(initialRole);

        if (initialRole) {
            setLoading(true);
            const defaultAdminPermissionsResponse = await apiDispatch(getProfileAccessRights({
                profileId: initialRole.profileId,
            }));
            if (defaultAdminPermissionsResponse.payload.success) {
                const availableAdminPermissions =
                    getAvailableAccessRights(defaultAdminPermissionsResponse.payload.data);
                setDefaultAdminPermissions(availableAdminPermissions);
            }

            if (defaultAdminPermissionsResponse.payload.error) {
                setLoadingError(defaultAdminPermissionsResponse.payload.data);
            }
            setLoading(false);
        }
    }, [initialRole, apiDispatch]);

    const resetAdminPermissions = useCallback(() => {
        onPermissionsChanged(defaultAdminPermissions);
    }, [defaultAdminPermissions, onPermissionsChanged]);

    const isRoleChanged = useMemo(() => (
        initialRole
            ? !isDeepEqual({prev: initialRole, current: role})
            : false
    ), [initialRole, role]);

    return {
        isLoading,
        loadingError,
        isRoleChanged,

        role, changeRole,
        initialRole, setInitialRole,

        defaultAdminPermissions,
        discardAdminRole,
        resetAdminPermissions,
    };
};
